import { Button, Classes, ControlGroup, MenuItem, Position } from '@blueprintjs/core';
import { Select } from '@blueprintjs/select';
import useUserContext from 'ecommerce/context/user';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import ancestorsApi from '../../api/ancestors';

const ButtonText = styled.span`
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const StyledButton = styled(Button)`
  justify-content: space-between;
  .bp5-button-text {
    overflow: hidden;
  }
`;

const getGenderIcon = gender => {
  switch (gender) {
    case 'f':
      return <img alt="female" src="/static/img/woman-20.png" />;
    case 'm':
      return <img alt="male" src="/static/img/man-20.png" />;
    default:
      return <img alt="" src="/static/img/question-mark-20.png" />;
  }
};

const itemPredicate = (query, person) => {
  return person.name.toLowerCase().indexOf(query.toLowerCase()) >= 0;
};

const renderOption = (person, { handleClick, modifiers }) => {
  const { birth, death, gender } = person;
  const birthYear = birth == null ? undefined : `* ${new Date(birth).getFullYear()}`;
  const deathYear = death == null ? undefined : `† ${new Date(death).getFullYear()}`;
  const label = [birthYear, deathYear].filter(value => value != null).join(' – ');
  return (
    <MenuItem
      active={modifiers.active}
      disabled={modifiers.disabled}
      key={person.id}
      icon={getGenderIcon(gender)}
      label={label}
      onClick={handleClick}
      text={person.name}
    />
  );
};

const PersonSuggest = ({ type = "ancestry", treeId, className, disabled, fill, onChange, selectedPerson }) => {
  const userContext = useUserContext();

  const abortControllerRef = useRef();

  const [persons, setPersons] = useState([]);

  const handleOnQueryChange = async (query, event) => {
    // remove cached people, when query is less than 3 chars
    if (query.length < 3) {
      setPersons(value => (value.length > 0 ? [] : value));
      return;
    }

    // don't do this twice
    if (!event) {
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const { data } = await ancestorsApi(
      type,
      userContext.accessToken,
      userContext.ancestryAuth,
    ).getTreePersons(treeId, query, {
      signal: abortController.signal,
    });

    if (data) {
      setPersons(data);
    }

    abortControllerRef.current = null;
  };

  return (
    <ControlGroup className={className} fill={fill}>
      <Select
        activeItem={selectedPerson}
        disabled={disabled}
        className={fill ? Classes.FILL : undefined}
        itemPredicate={itemPredicate}
        itemRenderer={renderOption}
        items={persons}
        noResults={null}
        onItemSelect={onChange}
        query=""
        inputProps={{
          placeholder: "Type at least 3 letters of the person's name",
          style: { minWidth: 330 },
        }}
        onQueryChange={handleOnQueryChange}
        popoverProps={{ minimal: false, position: Position.BOTTOM_LEFT, targetTagName: 'div' }}
      >
        <StyledButton
          disabled={disabled}
          fill={fill}
          rightIcon="caret-down"
          text={<ButtonText>{selectedPerson?.name ?? 'Select person'}</ButtonText>}
        />
      </Select>
      {selectedPerson && (
        <Button disabled={disabled} onClick={() => onChange(undefined)} icon="cross" />
      )}
    </ControlGroup>
  );
};

PersonSuggest.defaultProps = {
  className: undefined,
  disabled: false,
  fill: false,
  onChange: () => { },
  selectedPerson: undefined,
};

PersonSuggest.propTypes = {
  treeId: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  fill: PropTypes.bool,
  onChange: PropTypes.func,
  selectedPerson: PropTypes.object,
};

export default PersonSuggest;
